import { useEffect, useRef, useState } from "react"
import { SUPPORT_EMAIL } from "@shared/contact"
import { DONATION_ACCOUNT } from "@shared/donation"

function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
      <rect x="5.5" y="5.5" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10.5 5.5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v4.5a2 2 0 0 0 2 2h1.5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden>
      <path d="m3 8.5 3.2 3.2L13 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function DonateSection() {
  const [copied, setCopied] = useState(false)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current)
    },
    [],
  )

  const copyAccountNumber = async () => {
    try {
      await navigator.clipboard.writeText(DONATION_ACCOUNT.accountNumber)
      setCopied(true)
      if (resetTimer.current) clearTimeout(resetTimer.current)
      resetTimer.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access denied — the number is still visible on screen.
    }
  }

  return (
    <section className="bg-cream-50 pt-10 lg:pt-14">
      <div className="public-container">
        <div className="relative overflow-hidden rounded-[28px] bg-forest-950 px-6 py-10 sm:px-10 lg:grid lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-14 lg:rounded-[32px] lg:px-14 lg:py-14">
          <img
            src="/images/home/logo.png"
            alt=""
            aria-hidden
            className="pointer-events-none absolute -right-12 -top-12 h-64 w-64 opacity-[0.07] lg:h-80 lg:w-80"
          />

          <div className="relative">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-gold-500">
              <span className="h-1.5 w-1.5 rounded-full bg-gold-500" aria-hidden />
              Direct donation
            </span>
            <h2 className="mt-4 font-display text-[32px] font-bold leading-tight text-white sm:text-[40px]">
              Donate now.
            </h2>
            <p className="mt-3 max-w-md text-base leading-relaxed text-white/80">
              Prefer to give directly? You can transfer to the foundation's account — and we'll make sure it goes where it's needed most.
            </p>
            <p className="mt-5 text-sm leading-relaxed text-white/70">
              Email your transfer confirmation to{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="font-semibold text-gold-500 underline-offset-4 hover:underline"
              >
                {SUPPORT_EMAIL}
              </a>{" "}
              so we can send a receipt and updates on the children your gift supports.
            </p>
          </div>

          <div className="relative mt-8 rounded-2xl bg-cream-50 p-6 shadow-[0_24px_60px_rgba(0,0,0,0.28)] sm:p-7 lg:mt-0">
            <div className="h-1 w-16 rounded-full bg-gold-500" aria-hidden />
            <dl className="mt-5">
              <div className="flex items-baseline justify-between gap-4 border-b border-dashed border-forest-950/15 pb-4">
                <dt className="text-xs font-semibold uppercase tracking-[0.16em] text-ink/50">Bank</dt>
                <dd className="text-right text-sm font-semibold text-ink">{DONATION_ACCOUNT.bank}</dd>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 border-b border-dashed border-forest-950/15 py-4">
                <dt className="text-xs font-semibold uppercase tracking-[0.16em] text-ink/50">Account number</dt>
                <dd className="flex items-center gap-3">
                  <span className="font-display text-xl font-bold tracking-wide tabular-nums text-forest-950 sm:text-2xl">
                    {DONATION_ACCOUNT.accountNumber}
                  </span>
                  <button
                    type="button"
                    onClick={() => void copyAccountNumber()}
                    aria-label={copied ? "Account number copied" : "Copy account number"}
                    className="inline-flex items-center gap-1.5 rounded-full border border-forest-950/20 px-3 py-1.5 text-xs font-semibold text-forest-950 transition-colors hover:border-forest-950"
                  >
                    {copied ? <CheckIcon /> : <CopyIcon />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                </dd>
              </div>

              <div className="flex items-baseline justify-between gap-4 pt-4">
                <dt className="text-xs font-semibold uppercase tracking-[0.16em] text-ink/50">Account name</dt>
                <dd className="text-right text-sm font-semibold text-ink">{DONATION_ACCOUNT.accountName}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </section>
  )
}
